import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  Input,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  Modal,
  message,
} from "antd";
import { EditOutlined, DeleteOutlined, EyeOutlined } from "@ant-design/icons";
import { privateGetApi, privateDeleteApi } from "../../../utils/apiHelper";
import { hasPermission } from "../../../utils/rbac";
import useTableScrollY from "../../../hooks/useTableScrollY";
import TemplateQuickEditor from "../../../components/modals/templateQuickEditor";

const { Title, Text } = Typography;

const OWNERSHIP_OPTIONS = [
  { value: "", label: "All templates" },
  { value: "mine", label: "My templates" },
  { value: "public", label: "Public (other orgs)" },
  { value: "system", label: "System" },
];

// My templates first, then other orgs' public templates, then system —
// most-relevant-to-you first.
function ownershipGroup(record) {
  if (record.isOwner && !record.isSystem) return 0;
  if (record.isSystem) return 2;
  return 1;
}

export default function TemplatesList() {
  const canWrite = hasPermission("templates:write");
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [previewContent, setPreviewContent] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [quickEditId, setQuickEditId] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [ownershipFilter, setOwnershipFilter] = useState("");
  const [tableAreaRef, tableScrollY] = useTableScrollY([
    loading,
    templates.length,
  ]);

  const categoryOptions = useMemo(() => {
    const categories = Array.from(
      new Set(templates.map((t) => t.category).filter(Boolean)),
    ).sort();
    return [
      { value: "", label: "All categories" },
      ...categories.map((c) => ({ value: c, label: c })),
    ];
  }, [templates]);

  const visibleTemplates = useMemo(() => {
    const search = searchText.trim().toLowerCase();
    return templates
      .filter((t) => {
        if (search) {
          const haystack = `${t.name} ${t.subject || ""}`.toLowerCase();
          if (!haystack.includes(search)) return false;
        }
        if (categoryFilter && t.category !== categoryFilter) return false;
        if (ownershipFilter === "mine" && !(t.isOwner && !t.isSystem))
          return false;
        if (ownershipFilter === "public" && !(t.isOwner === false && !t.isSystem))
          return false;
        if (ownershipFilter === "system" && !t.isSystem) return false;
        return true;
      })
      .sort((a, b) => ownershipGroup(a) - ownershipGroup(b));
  }, [templates, searchText, categoryFilter, ownershipFilter]);

  const fetchTemplates = useCallback(() => {
    setLoading(true);
    privateGetApi("/templates", {})
      .then((res) => setTemplates(res?.data?.result || []))
      .catch(() => setTemplates([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const handlePreview = async (id) => {
    try {
      const res = await privateGetApi(`/templates/${id}`, {});
      setPreviewContent(res?.data?.result?.content || "");
    } catch {
      message.error("Failed to load template preview.");
    }
  };

  const handleDelete = async (id) => {
    try {
      setDeletingId(id);
      await privateDeleteApi(`/templates/${id}`, {});
      message.success("Template deleted.");
      fetchTemplates();
    } catch {
      message.error("Failed to delete template.");
    } finally {
      setDeletingId(null);
    }
  };

  const confirmDelete = (record) => {
    Modal.confirm({
      title: "Delete template",
      content: `Delete "${record.name}"? This can't be undone.`,
      okText: "Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk: () => handleDelete(record.id),
    });
  };

  const visibilityTag = (record) => {
    if (record.isSystem) return <Tag>System</Tag>;
    if (record.isOwner)
      return (
        <Tag color="cyan">
          My {record.visibility === "public" ? "(Public)" : "(Private)"}
        </Tag>
      );
    return <Tag color="green">Public</Tag>;
  };

  const columns = [
    { title: "Name", dataIndex: "name", key: "name" },
    {
      title: "Subject",
      dataIndex: "subject",
      key: "subject",
      render: (subject) =>
        subject || <Text type="secondary">No subject</Text>,
    },
    { title: "Category", dataIndex: "category", key: "category" },
    {
      title: "Visibility",
      key: "visibility",
      render: (_, record) => visibilityTag(record),
    },
    {
      title: "Action",
      key: "action",
      width: 160,
      render: (_, record) => (
        <Space size="small">
          <Button
            type="text"
            icon={<EyeOutlined />}
            title="Preview"
            onClick={() => handlePreview(record.id)}
          />
          {canWrite && record.isOwner && !record.isSystem ? (
            <>
              <Button
                type="text"
                icon={<EditOutlined />}
                title="Edit"
                onClick={() => setQuickEditId(record.id)}
              />
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                title="Delete"
                loading={deletingId === record.id}
                onClick={() => confirmDelete(record)}
              />
            </>
          ) : null}
        </Space>
      ),
    },
  ];

  return (
    <div className="Templates">
      <Space
        wrap
        style={{
          display: "flex",
          margin: "0 0.5rem 0.5rem 0.5rem",
          justifyContent: "space-between",
        }}
      >
        <div>
          <Title level={5} style={{ marginBottom: 0 }}>
            Templates
          </Title>
          <Text type="secondary">
            Manage the email templates your org has saved. Create new ones
            from the Quick Campaign flow&apos;s &quot;Save as
            Template&quot; action.
          </Text>
        </div>
      </Space>

      <Space wrap style={{ margin: "0 0.5rem 1rem 0.5rem", width: "100%" }}>
        <Input.Search
          placeholder="Search name or subject"
          allowClear
          style={{ minWidth: 240 }}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
        />
        <Select
          style={{ minWidth: 180 }}
          value={categoryFilter}
          options={categoryOptions}
          onChange={setCategoryFilter}
        />
        <Select
          style={{ minWidth: 180 }}
          value={ownershipFilter}
          options={OWNERSHIP_OPTIONS}
          onChange={setOwnershipFilter}
        />
      </Space>

      <div ref={tableAreaRef} style={{ width: "100%" }}>
        <Table
          rowKey="id"
          loading={loading}
          dataSource={visibleTemplates}
          columns={columns}
          scroll={{ x: 900, y: tableScrollY }}
          pagination={false}
        />
      </div>

      <Modal
        title="Template Preview"
        open={previewContent !== null}
        onCancel={() => setPreviewContent(null)}
        footer={null}
        width="min(1000px, 90vw)"
      >
        <iframe
          title="template-preview"
          style={{ width: "100%", height: 600, border: "1px solid #eee" }}
          srcDoc={previewContent || ""}
        />
      </Modal>

      <TemplateQuickEditor
        templateId={quickEditId}
        open={quickEditId !== null}
        onClose={() => setQuickEditId(null)}
        onSaved={fetchTemplates}
      />
    </div>
  );
}
